/**
 * The agent, beside the conversation (V2Bureau, right column) and in a sheet on a phone (V2Mobile): its living
 * character and name, then « Détails » (routines with their switches: the morning brief, the evening recap, each watch,
 * each recurring mission; what runs right now), « Mémoire » (its newest memories, « Tout voir », « Coffre ») and
 * « Ordinateur » (its cloud computer's state, « Voir en direct »). On a phone the sheet keeps the routines and four tiles.
 */
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { AgentSettings, MachineView, MemoryItemView, MissionView } from "../../shared/contracts";
import type { WatchesView } from "../../shared/watches";
import type { ShellAgent } from "../lib/agents";
import { usePendingApprovals, useMachines, useRuns, useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { isRunActive, scheduleWords, spokenHm } from "../lib/format";
import { useNow } from "../lib/hooks";
import { useMainMood } from "../lib/mood";
import { invalidate, mutateResource, useResource } from "../lib/store";
import { AgentAvatarView, type CharacterMood } from "./avatar";
import { IconMemory, IconPlugMock, IconScreen, IconSmile, IconTune, IconVault } from "./icons";
import { cx, ROUND_BUTTON } from "./ui";

type Tab = "details" | "memoire" | "ordi";
const TABS: { id: Tab; label: string }[] = [
  { id: "details", label: "Détails" },
  { id: "memoire", label: "Mémoire" },
  { id: "ordi", label: "Ordinateur" },
];

export function useWatches() {
  return useResource<WatchesView>("watches", async (signal) => api("GET /api/watches", { signal }), { staleMs: 30_000 });
}

export function useMissionsList() {
  return useResource<MissionView[]>("missions", async (signal) => (await api("GET /api/missions", { signal })).missions, {
    refreshOn: ["mission.upsert"],
    staleMs: 30_000,
  });
}

function useRecentMemories() {
  return useResource<MemoryItemView[]>("memory:recent", async (signal) => (await api("GET /api/memory", { query: { status: "active" }, signal })).items, { staleMs: 30_000 });
}

/** « Toutes les 3 heures » → « Toutes les 3 h ». */
export function shortRhythm(text: string): string {
  const t = text.replace(/\bheures?\b/g, "h").replace(/\bminutes\b/g, "min").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** The agent's mood in the panel: the live one for the main agent. */
function useAgentMood(agent: ShellAgent): CharacterMood {
  const live = useMainMood();
  return agent.main ? live : "repos";
}

/** The right column of the desktop (≥ 1200 px). */
export function AgentPanel({ agent }: { agent: ShellAgent }) {
  const [tab, setTab] = useState<Tab>("details");
  const mood = useAgentMood(agent);
  return (
    <div className="flex min-h-full flex-col gap-4 px-5 pt-[26px] pb-6">
      <div className="flex flex-col items-center gap-1">
        <AgentAvatarView agent={agent} size={128} bare mood={mood} label={`${agent.name}, ton agent`} />
        <h2 className="max-w-full truncate font-display text-[28px] leading-tight">{agent.name}</h2>
      </div>
      <div role="tablist" aria-label={agent.name} className="flex justify-center gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`agent-tab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`agent-tabpanel-${t.id}`}
            onClick={() => setTab(t.id)}
            className={cx("h-8 rounded-full px-3 text-[14px] font-medium transition", tab === t.id ? "bg-bubble-user text-fg" : "text-muted hover:text-fg")}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`agent-tabpanel-${tab}`} aria-labelledby={`agent-tab-${tab}`} className="flex flex-col gap-2">
        {tab === "details" ? agent.main ? <MainDetails /> : <BotDetails agent={agent} /> : null}
        {tab === "memoire" ? <MemoryTab /> : null}
        {tab === "ordi" ? agent.main ? <ComputerTab agent={agent} /> : <BotComputerTab agent={agent} /> : null}
      </div>
    </div>
  );
}

function Heading({ children, to, label }: { children: ReactNode; to?: "/veilles" | "/missions"; label?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 px-1 pt-1.5">
      <h3 className="text-[13px] font-semibold text-subtle">{children}</h3>
      {to ? (
        <Link to={to} className="-my-2 inline-flex min-h-9 min-w-11 items-center justify-end text-[13px] text-muted hover:text-fg max-md:min-h-11">
          {label ?? "Tout voir"}
        </Link>
      ) : null}
    </div>
  );
}

/** « Routines » and « En cours » of the main agent. */
function MainDetails() {
  return (
    <>
      <Heading to="/veilles" label="Veilles">
        Routines
      </Heading>
      <Routines />
      <InProgress />
    </>
  );
}

interface Routine {
  key: string;
  title: string;
  when: string;
  on: boolean;
  toggle: (on: boolean) => Promise<void>;
}

/** The routines with their switches (the brief and recap, each watch, each recurring mission). */
export function Routines({ className }: { className?: string }) {
  const settings = useSettings();
  const watches = useWatches();
  const missions = useMissionsList();
  const now = useNow(60_000);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const s = settings.data;

  async function patchSettings(body: Partial<Pick<AgentSettings, "briefEnabled" | "recapEnabled">>) {
    mutateResource<AgentSettings>("settings", (d) => (d ? { ...d, ...body } : d));
    try {
      const next = await api("PATCH /api/settings", { body });
      mutateResource<AgentSettings>("settings", () => next);
      invalidate("dashboard");
    } catch (err) {
      void settings.reload();
      throw err;
    }
  }

  const rows: Routine[] = [];
  if (s) {
    rows.push({ key: "brief", title: "Brief du matin", when: spokenHm(s.briefTime), on: s.briefEnabled, toggle: (on) => patchSettings({ briefEnabled: on }) });
    rows.push({ key: "recap", title: "Récap du soir", when: spokenHm(s.recapTime), on: s.recapEnabled, toggle: (on) => patchSettings({ recapEnabled: on }) });
  }
  for (const w of watches.data?.watches ?? []) {
    rows.push({
      key: `w:${w.id}`,
      title: w.label,
      when: shortRhythm(w.scheduleText),
      on: w.status === "active",
      toggle: async (on) => {
        watches.mutate((d) => (d ? { ...d, watches: d.watches.map((x) => (x.id === w.id ? { ...x, status: on ? "active" : "paused" } : x)) } : d));
        try {
          const next = await api("PATCH /api/watches/:id", { params: { id: w.id }, body: { status: on ? "active" : "paused" } });
          watches.mutate(() => next);
        } catch (err) {
          void watches.reload();
          throw err;
        }
      },
    });
  }
  for (const m of missions.data ?? []) {
    if (!m.cadenceMinutes || (m.status !== "active" && m.status !== "paused")) continue;
    rows.push({
      key: `m:${m.id}`,
      title: m.title,
      when: scheduleWords(m.cadenceMinutes, m.nextRunAt, now) ?? "",
      on: m.status === "active",
      toggle: async (on) => {
        const next = await api("PATCH /api/missions/:id", { params: { id: m.id }, body: { status: on ? "active" : "paused" } });
        missions.mutate((list) => list?.map((x) => (x.id === next.id ? next : x)));
      },
    });
  }

  if (!rows.length) return null;
  return (
    <div className={cx("rounded-2xl bg-bg px-3.5", className)}>
      {rows.map((r, i) => (
        <div key={r.key} className={cx("flex min-h-[54px] items-center gap-2.5 py-2", i < rows.length - 1 && "border-b border-line")}>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14.5px]">{r.title}</span>
            {r.when ? <span className="block truncate text-[12.5px] text-subtle">{r.when}</span> : null}
          </span>
          <input
            type="checkbox"
            className="switch"
            aria-label={r.title}
            checked={r.on}
            disabled={busy === r.key}
            onChange={(e) => {
              const on = e.target.checked;
              setBusy(r.key);
              setError(null);
              r.toggle(on)
                .catch((err: unknown) => setError(errorText(err)))
                .finally(() => setBusy(null));
            }}
          />
        </div>
      ))}
      {error ? (
        <p role="alert" className="pb-2 text-[12.5px] text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** What runs right now: one line each, a thin bar while it works; a tap opens the run. */
export function InProgress() {
  const runs = useRuns({ poll: false });
  const active = (runs.data ?? []).filter((r) => isRunActive(r.status)).slice(0, 4);
  if (!active.length) return null;
  return (
    <>
      <Heading to="/missions" label="Missions">
        En cours
      </Heading>
      <div className="flex flex-col gap-1">
        {active.map((run) => (
          <Link key={run.id} to="/runs/$id" params={{ id: run.id }} className="flex flex-col gap-2 rounded-2xl bg-bg px-3.5 py-3 transition hover:bg-[#161616]">
            <span className="truncate text-[14.5px]">{run.title}</span>
            {run.status === "waiting_user" || run.status === "waiting_approval" ? (
              <span className="text-[12.5px] text-accent">{run.status === "waiting_user" ? "Une question pour toi" : "Attend ton accord"}</span>
            ) : (
              <span aria-hidden="true" className="progress-glide" />
            )}
          </Link>
        ))}
      </div>
    </>
  );
}

/** The six newest memories as chips, then « Tout voir » and « Coffre ». */
function MemoryTab() {
  const memories = useRecentMemories();
  const items = (memories.data ?? []).slice(0, 6);
  return (
    <div className="flex flex-col gap-2.5">
      {items.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Souvenirs récents">
          {items.map((m) => (
            <li key={m.id} title={m.content} className="max-w-full truncate rounded-[15px] bg-bg px-[11px] py-[7px] text-[13.5px]">
              {chip(m.content)}
            </li>
          ))}
        </ul>
      ) : memories.loading ? null : (
        <p className="px-1 text-[13.5px] text-muted">Rien encore.</p>
      )}
      <div className="flex gap-2">
        <Link to="/memoire" className="inline-flex h-[34px] items-center rounded-full bg-chip px-3.5 text-[13.5px] font-semibold transition hover:bg-[#333333]">
          Tout voir
        </Link>
        <Link to="/coffre" className="inline-flex h-[34px] items-center rounded-full bg-chip px-3.5 text-[13.5px] font-semibold transition hover:bg-[#333333]">
          Coffre
        </Link>
      </div>
    </div>
  );
}

function chip(text: string): string {
  const t = text.replace(/\s+/g, " ").replace(/[.。]$/, "").trim();
  return t.length > 34 ? `${t.slice(0, 33).trimEnd()}…` : t;
}

/** « En direct », « Démarre… », « Éteint ». */
export function computerState(m: MachineView | undefined): string {
  if (!m) return "Pas d'ordinateur";
  switch (m.state) {
    case "running":
    case "online":
      return "En direct";
    case "starting":
      return "Démarre…";
    case "stopping":
      return "S'éteint…";
    case "unavailable":
      return "Indisponible";
    default:
      return "Éteint";
  }
}

/** The cloud computer: a 16:10 tile with its state, « Voir en direct » (the live screen alone, /ecran). */
function ComputerTab({ agent }: { agent: ShellAgent }) {
  const machines = useMachines();
  const cloud = (machines.data ?? []).find((m) => m.kind === "cloud");
  const live = cloud?.state === "running";
  return (
    <div className="flex flex-col gap-2.5">
      <div className="relative flex aspect-[16/10] flex-col items-center justify-center gap-2 overflow-hidden rounded-[14px] bg-[#1c1b29]">
        <AgentAvatarView agent={agent} size={44} bare mood={cloud?.agentActive ? "parle" : "repos"} />
        {cloud ? <span className="max-w-[85%] truncate text-[12.5px] text-muted">{cloud.name}</span> : null}
      </div>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={cx("h-[7px] w-[7px] shrink-0 rounded-full", live ? "live-dot bg-accent" : "bg-line-strong")} />
        <span className="min-w-0 flex-1 truncate text-[13.5px] text-muted">{computerState(cloud)}</span>
        {cloud?.liveView ? (
          <Link to="/ecran" className="inline-flex h-8 shrink-0 items-center rounded-full bg-ink px-3.5 text-[13.5px] font-semibold text-ink-fg transition hover:bg-ink/88">
            Voir en direct
          </Link>
        ) : (
          <Link to="/ordinateur" className="inline-flex h-8 shrink-0 items-center rounded-full bg-chip px-3.5 text-[13.5px] font-semibold transition hover:bg-[#333333]">
            Ordinateur
          </Link>
        )}
      </div>
    </div>
  );
}

/** An extra agent: its own computer lives on its page. */
function BotComputerTab({ agent }: { agent: ShellAgent }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex aspect-[16/10] items-center justify-center rounded-[14px] bg-[#1c1b29]">
        <AgentAvatarView agent={agent} size={44} bare />
      </div>
      <Link to="/agents/$id" params={{ id: agent.id }} hash="ordinateur" className="inline-flex h-8 items-center self-start rounded-full bg-chip px-3.5 text-[13.5px] font-semibold transition hover:bg-[#333333]">
        Son ordinateur
      </Link>
    </div>
  );
}

function BotDetails({ agent }: { agent: ShellAgent }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="px-1 text-[13.5px] text-muted">Une seule mémoire, son propre ordinateur.</p>
      <Link to="/agents/$id" params={{ id: agent.id }} hash="reglages" className="inline-flex h-[34px] items-center self-start rounded-full bg-chip px-3.5 text-[13.5px] font-semibold transition hover:bg-[#333333]">
        Ses réglages
      </Link>
    </div>
  );
}

const TILES: { to: "/ordinateur" | "/memoire" | "/coffre" | "/agent"; label: string; icon: typeof IconScreen }[] = [
  { to: "/ordinateur", label: "Ordinateur", icon: IconScreen },
  { to: "/memoire", label: "Mémoire", icon: IconMemory },
  { to: "/coffre", label: "Coffre", icon: IconVault },
  { to: "/agent", label: "Son visage", icon: IconSmile },
];

/**
 * The agent's sheet (V2Mobile): from the agent button of the composer on a phone (and from the conversation's head
 * between 761 and 1199 px): the character, its name, Connexions and Réglages, the routines, four tiles.
 */
export function AgentSheet({ agent, open, onClose }: { agent: ShellAgent; open: boolean; onClose: () => void }) {
  const pending = usePendingApprovals();
  const pendingCount = pending.data?.length ?? 0;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 h-full w-full bg-[var(--dim)]" />
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={agent.name}
        className="sheet-up absolute inset-x-0 bottom-0 flex max-h-[92dvh] flex-col gap-3 overflow-y-auto rounded-t-[26px] bg-elev px-4 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+22px)] outline-none md:inset-x-auto md:top-0 md:right-0 md:bottom-0 md:max-h-none md:w-[380px] md:rounded-t-none md:rounded-l-[26px] md:pt-6"
      >
        <span aria-hidden="true" className="h-[5px] w-[38px] self-center rounded-full bg-line-strong md:hidden" />
        <div className="flex items-center gap-3">
          <AgentAvatarView agent={agent} size={60} bare mood="joie" />
          <span className="min-w-0 flex-1 truncate font-display text-[26px] leading-tight">{agent.name}</span>
          <Link to="/connexions" aria-label="Connexions" onClick={onClose} className={ROUND_BUTTON}>
            <IconPlugMock width={16} height={16} strokeWidth={2} />
          </Link>
          <Link to="/reglages" aria-label="Réglages" onClick={onClose} className={ROUND_BUTTON}>
            <IconTune width={16} height={16} strokeWidth={2} />
          </Link>
        </div>
        {agent.main ? <Routines /> : null}
        {pendingCount > 0 ? (
          <Link to="/approbations" onClick={onClose} className="flex min-h-12 items-center justify-between rounded-2xl bg-bg px-3.5 text-[15px]">
            Validations
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-accent px-1.5 text-[12.5px] font-semibold text-accent-fg">{pendingCount}</span>
          </Link>
        ) : null}
        {agent.main ? (
          <div className="flex flex-col gap-1">
            <InProgress />
          </div>
        ) : null}
        <div className="grid grid-cols-4 gap-2">
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.to} to={t.to} onClick={onClose} className="flex h-[68px] flex-col items-center justify-center gap-1.5 rounded-2xl bg-bg text-[12.5px] font-medium transition hover:bg-[#161616]">
                <Icon width={20} height={20} strokeWidth={1.9} />
                {t.label}
              </Link>
            );
          })}
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 text-[13px] text-muted">
          <Link to="/missions" onClick={onClose} className="inline-flex min-h-11 min-w-11 items-center justify-center hover:text-fg">
            Missions
          </Link>
          <Link to="/veilles" onClick={onClose} className="inline-flex min-h-11 min-w-11 items-center justify-center hover:text-fg">
            Veilles
          </Link>
          <Link to="/agents" onClick={onClose} className="inline-flex min-h-11 min-w-11 items-center justify-center hover:text-fg">
            Mes agents
          </Link>
        </div>
      </div>
    </div>
  );
}
